import React, { useCallback, useEffect, useRef, useState } from 'react'
import Drawer from "react-drag-drawer";
import { FiX, FiLock } from 'react-icons/fi';
import { playSong } from '../../utils/song'
import songsName from '../../enums/songsName'
import api from '../../services/api';
import Loading from '../loading';
import { useFormik } from 'formik';
import Button from '../button'
import { debounce, delay } from '../../utils/common';
import clsx from 'clsx';
import Loader from 'react-loader-spinner'
import toast from 'react-hot-toast';
import { PRODUCT } from '../../consts/product';
import axios from 'axios';
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  name: yup.string().required('O nome é obrigatório'),
  email: yup.string().required('O e-mail é obrigatório').email('E-mail com formato inválido'),
  emailConfirmation: yup.string().required('A confirmação do e-mail é obrigatória').email('E-mail com formato inválido').oneOf([yup.ref('email'), null], 'O e-mail e a confirmação do e-mail devem ser iguais'),
  password: yup.string().required('A senha é obrigatória'),
  passwordConfirmation: yup.string().required('A confirmação da senha é obrigatória').oneOf([yup.ref('password'), null], 'A senha e a confirmação da senha devem ser iguais'),
})

const RegistrationModal = (props) => {
  const isOpenedRef = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [formFields, setFormFields] = useState({
    general: [],
    payment: []
  })

  const loadRegistrationFormFields = useCallback(async () => {
    const { data } = await api.get('/vamole/registrationformfields')
    setFormFields(data)
  }, [])

  useEffect(() => {
    if (props.isOpened && !isOpenedRef.current) {
      setIsLoading(true)

      Promise.all([
        loadRegistrationFormFields(),
      ]).finally(() => {
        setIsLoading(false)
      })
    }
  
    isOpenedRef.current = props.isOpened
  }, [loadRegistrationFormFields, props.isOpened])

  const getInitialFormFieldValues = () => {
    const values = {}

    formFields.general.forEach(field => {
      values[field.name] = ''
    })
    formFields.payment.forEach(field => {
      values[field.name] = ''
    })

    return values
  }

  const openLinkHandler = (link) => () => {
    if (window.in_native_app) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'OPEN_LINK',
        payload: {
          uri: link
        }
      }))
    }
  }

  const {
    errors,
    touched,
    resetForm,
    handleBlur,
    handleSubmit,
    handleChange,
  } = useFormik({
    initialValues: getInitialFormFieldValues(),
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true)

        const payload = {
          email: values.email,
          nome: values.name,
          pwd: values.password,
          msisdn: '+55900000000',
          produto: PRODUCT,
        }

        await api.post('/app/conta', payload)

        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'PURCHASE_LOGIN',
          payload: {
            identifier: payload.email
          }
        }))

        await delay(200)
        debounce(() => {
          toast.success("Conta criada com sucesso, vamos fazer o pagamento da sua assinatura", { duration: 4000 })
        }, 500)

        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'PURCHASE_SUBSCRIPTION',
        }))
      } catch (error) {
        setIsSubmitting(false)
        debounce(() => {
          toast.error(error.response.data.msg || "Tivemos um erro ao tentar fazer sua assinatura, por favor tente novamente mais tarde.", { duration: 4000 })
        }, 500)
      }
    }
  })

  const closeHandler = () => {
    playSong(songsName.TAP)
    setIsSubmitting()
    resetForm()
    props.close()
  }

  const reactNativeMessageHandler = useCallback(async (data) => {
    if (data.type === 'PURCHASE_SUCCESS') {
      try {
        resetForm()

        debounce(() => {
          toast.success("Pagamento realizado com sucesso", { duration: 4000 })
        }, 500)
      } catch {
        debounce(() => {
          toast.error('Não foi possível finalizar a compra da sua assinatura')
        }, 500)
      } finally {
        setIsSubmitting(false)
      }
    }

    if (data.type === 'PURCHASE_ERROR') {
      if (!data.payload.includes('cancelled')) {
        debounce(() => {
          toast.error('Não foi possível realizar a compra da sua assinatura')
        }, 500)
      }

      setIsSubmitting(false)
    }
  }, [resetForm, setIsSubmitting])

  useEffect(() => {
    document.addEventListener('message', event => {
			let data = event.data

			if (typeof data === 'string') {
				data = JSON.parse(data)
			}

			reactNativeMessageHandler(data)
		})

    window.addEventListener('message', (event) => {
			let data = event.data

			if (typeof data === 'string') {
				data = JSON.parse(data)
			}

			reactNativeMessageHandler(data)
    })
  }, [reactNativeMessageHandler])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className='flex flex-1 w-full flex-col justify-center items-center'>
          <Loading />
        </div>
      )
    }

    return (
      <div className='w-full flex flex-col justify-start items-start'>
        <div className='w-full'>
          <form onSubmit={handleSubmit} className='w-full flex flex-col justify-start items-start'>
            <div className="w-full p-6 rounded-2xl bg-blue-100 mt-6 mb-6">
              <p className="text-2xl text-center text-blue-400 font-bold">{window.subscription_plan.product.title}</p>
              <p className="text-2xl text-center text-blue-400 font-bold">{window.subscription_plan.product.pricePerMonthString}/Mês</p>

              <p className="mt-4 text-lg text-gray-600">
                <b>Dois novos livros por mês</b>
                <br />
                para ler e ouvir.
              </p>

              <p className="mt-4 text-lg text-gray-600">
                <b>Uma biblioteca de presente</b>
                <br />
                na efetivação da assinatura.
              </p>

              <p className="mt-4 text-lg text-gray-600">
                <b>Leia online ou offline</b>
                <br />e guarde seus livros para sempre.
              </p>

              <p className="mt-4 text-lg text-gray-600">
                <b>Cancele quando quiser.</b>
              </p>
            </div>

            <div className='w-full space-y-4'>
              <h2 className="font-bold text-lg">Dados pessoais</h2>
              <hr className="w-full h-px mt-2 mb-6 bg-gray-200 border-0" />

              <div className='w-full'>
                <span className='mb-1 text-sm font-bold'>Nome</span>
                <input
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className='bg-gray-50 border border-gray-primary text-sm rounded-lg block w-full p-2.5'
                  name="name"
                  label="Nome"
                  placeholder="Digite seu nome"
                />

                {errors.name && touched.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              <div className='w-full'>
                <span className='mb-1 text-sm font-bold'>E-mail</span>
                <input
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className='bg-gray-50 border border-gray-primary text-sm rounded-lg block w-full p-2.5'
                  name="email"
                  type="email"
                  label="E-mail"
                  placeholder="Digite seu e-mail"
                />

                {errors.email && touched.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              <div className='w-full'>
                <span className='mb-1 text-sm font-bold'>Confirmar e-mail</span>
                <input
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className='bg-gray-50 border border-gray-primary text-sm rounded-lg block w-full p-2.5'
                  name="emailConfirmation"
                  type="email"
                  label="Confirmar e-mail"
                  placeholder="Repita seu e-mail"
                  error={
                    errors.emailConfirmation
                      ? errors.emailConfirmation.message
                      : undefined
                  }
                />

                {errors.emailConfirmation && touched.emailConfirmation && (
                  <p className="text-xs text-red-500">{errors.emailConfirmation}</p>
                )}
              </div>

              <div className='w-full'>
                <span className='mb-1 text-sm font-bold'>Senha</span>
                <input
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className='bg-gray-50 border border-gray-primary text-sm rounded-lg block w-full p-2.5'
                  name="password"
                  type="password"
                  label="Senha"
                  placeholder="Digite sua senha"
                />

                {errors.password && touched.password && (
                  <p className="text-xs text-red-500">{errors.password}</p>
                )}
              </div>

              <div className='w-full'>
                <span className='mb-1 text-sm font-bold'>Confirmar senha</span>
                <input
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className='bg-gray-50 border border-gray-primary text-sm rounded-lg block w-full p-2.5'
                  name="passwordConfirmation"
                  label="Confirmar senha"
                  placeholder="Repita sua senha"
                  type="password"
                />

                {errors.passwordConfirmation && touched.passwordConfirmation && (
                  <p className="text-xs text-red-500">{errors.passwordConfirmation}</p>
                )}
              </div>
            </div>

            <div className='mt-6 mx-auto'>
              <Button
                type="submit"
                disabled={(isSubmitting || Object.keys(errors).length)}
                onMouseEnter={() => {
                  playSong(songsName.TAP)
                }}
                onClick={() => {
                  playSong(songsName.TAP)
                }}
              >
                {isSubmitting ? (
                  <Loader
                    type="Oval"
                    visible={true}
                    color="white"
                    width={16}
                    height={16}
                    className="flex flex-1 justify-center items-center"
                  />
                ) : "Assinar"}
              </Button>
            </div>

            <div className='my-4 self-center text-center text-sm'>
              Ao se registrar, você concorda com os <button type='button' className='text-blue-primary underline font-bold' onClick={openLinkHandler('https://dumdumlivros.n3r.com.br/?page=termos-de-uso')}>Termos de Uso</button> e a <button type='button' className='text-blue-primary underline font-bold' onClick={openLinkHandler('https://dumdumlivros.n3r.com.br/?page=politica-de-privacidade')}>Política de Privacidade</button>
            </div>

            <div className='w-full flex flex-col justify-start items-center mt-6'>
              <FiLock size={24} />
              <p className='text-xs mt-2'>Todos os dados são criptografados</p>
            </div>
          </form>
        </div>
      </div>
    )
  }
    
  return (
    <Drawer
      dontApplyListeners={true}
      allowClose={false}
      open={props.isOpened}
      onRequestClose={props.close}
      direction="left"
      modalElementClass="absolute top-0 left-0"
    >
      <div
        className={clsx('w-screen max-w-lg h-screen bg-modal flex flex-col justify-start items-start px-4 text-lg overflow-y-auto')}
        style={{
          paddingTop: window.native_app_status_bar_height ?? 16,
          paddingBottom: window.native_app_navigation_bar_height ?? 16
        }}
      >
        <div className='w-full flex flex-row items-center justify-between mb-6'>
          <h2 className='font-bold uppercase'>Assinar</h2>

          <button
            type="button"
            onMouseEnter={() => {
              playSong(songsName.TAP)
            }}
            onClick={closeHandler}
            className="text-black bg-transparent hover:bg-slate-200 rounded-lg text-2xl lg:text-xl ml-auto"
          >
            <FiX />
          </button>
        </div>

        {renderContent()}
      </div>
    </Drawer>
  )
}

export default RegistrationModal